import React, { useCallback, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useQuery } from '@tanstack/react-query'
import echarts from '../config'
import { cssVar } from '../../../../utils/cssVar'
import FadeIn from '../../../molecules/Transitions/FadeIn'
import Loading from '../../../molecules/Loading'

function applyChartConfig (data, options = {}) {
  const chartOptions = options?.chartOptions ?? {}

  return {
    textStyle: {
      fontFamily: chartOptions?.textStyle?.fontFamily ?? cssVar('--font-sans')
    },
    tooltip: {
      trigger: 'item',
      showDelay: 0,
      transitionDuration: 0.2,
      formatter: options.valueFormat ? (params) => {
        return `${params.seriesName}<br />${params.name} ${options.valueFormat(params.value)}`
      } : undefined
    },
    visualMap: {
      left: 'right',
      min: data.extents.min,
      max: data.extents.max,
      inRange: {
        color: chartOptions?.visualMap?.inRange?.color ?? [
          '#4575b4',
          '#74add1',
          '#abd9e9',
          '#e0f3f8',
          '#ffffbf',
          '#fee090',
          '#fdae61',
          '#f46d43',
          '#d73027',
          '#a50026'
        ]
      },
      text: ['High', 'Low'],
      calculable: true,
      formatter: options.valueFormat || undefined
    },
    toolbox: {
      show: chartOptions?.toolbox?.show || false,
      left: 'left',
      top: 'top',
      feature: {
        restore: {},
        saveAsImage: {}
      }
    },
    selectedMode: chartOptions?.selectedMode ?? 'single',
    itemStyle: {
      shadowColor: 'rgba(0, 0, 0, 0.5)',
      shadowBlur: 3,
      shadowOffsetX: 1,
      shadowOffsetY: 2
    },
    series: [
      {
        name: data.name,
        type: 'map',
        roam: false,
        map: 'USA',
        layoutCenter: chartOptions?.series?.layoutCenter ?? ['50%', '50%'], // center the map
        layoutSize: chartOptions?.series?.layoutSize ?? '175%', // fill the container
        emphasis: {
          label: {
            show: true
          }
        },
        label: {
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          borderRadius: 4,
          padding: [4, 6],
          color: '#303030',
          fontWeight: 'bold',
          emphasis: {
            show: true
          }
        },
        data: data.series
      }
    ]
  }
}

function ChloroplethChart ({ height, width, valueFormat, data, chartOptions }) {
  const chartRef = useRef(null)
  const chartInstanceRef = useRef(null)
  const { data: geoMap, isLoading } = useQuery({
    queryKey: ['chloropleth_geoMap'],
    queryFn: async () => {
      const response = await fetch('/maps/usa_geo.json')
      if (response.status === 200) {
        return response.json()
      } else {
        throw new Error('Unable to fetch geo map')
      }
    }
  })

  useEffect(() => {
    if (chartRef.current && !chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(chartRef.current)
      window.addEventListener('resize', chartInstanceRef.current.resize)
    }

    return () => {
      if (chartInstanceRef.current) {
        window.removeEventListener('resize', chartInstanceRef.current.resize)
        chartInstanceRef.current.dispose()
        chartInstanceRef.current = null
      }
    }
  }, [])

  const registerMap = useCallback(() => {
    console.log('registering map')
    if (geoMap) {
      echarts.registerMap('USA', geoMap, {
        Alaska: {
          left: -140,
          top: 45,
          width: 15
        },
        Hawaii: {
          left: -132,
          top: 30,
          width: 5
        },
        'Puerto Rico': {
          left: -76,
          top: 26,
          width: 2
        }
      })

      return true
    }
  }, [geoMap])

  const setOptions = useCallback(() => {
    if (chartInstanceRef.current && data) {
      console.log('chart options')
      const chart = chartInstanceRef.current
      chart.setOption(applyChartConfig(data || [], {
        valueFormat,
        chartOptions
      }))

      return true
    }
  }, [data, valueFormat, chartOptions])

  useEffect(() => {
    const mapRegistered = registerMap()
    if (mapRegistered) {
      setOptions()
    }
  }, [setOptions, registerMap, isLoading, data])

  return (
    <FadeIn style={{ height, width }}>
      <div style={{ height, width }} ref={chartRef} />
      {isLoading ? <Loading /> : null}
    </FadeIn>
  )
}

ChloroplethChart.propTypes = {
  height: PropTypes.number,
  width: PropTypes.number,
  data: PropTypes.shape({
    series: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.any,
      name: PropTypes.string
    })),
    extents: PropTypes.shape({
      min: PropTypes.number,
      max: PropTypes.number
    })
  }),
  valueFormat: PropTypes.func,
  chartOptions: PropTypes.object
}

export default ChloroplethChart
